// src/pages/KarigarManagement/Job/constants/jobConstants.js

import {
  Sparkles,
  Wrench,
  Ruler,
  Gem,
  Package,
  Briefcase,
  FileText,
  Settings,
  Calendar,
  CheckCircle,
  Image as ImageIcon,
} from "lucide-react";

// ============================================
// JOB TYPES
// ============================================

export const JOB_TYPES = [
  {
    value: "CUSTOM_ORDER",
    label: "Custom Order",
    icon: Sparkles,
    description: "New jewelry making from scratch",
    color: "bg-purple-100 text-purple-700",
  },
  {
    value: "REPAIR",
    label: "Repair",
    icon: Wrench,
    description: "Fix broken or damaged jewelry",
    color: "bg-orange-100 text-orange-700",
  },
  {
    value: "ALTERATION",
    label: "Alteration",
    icon: Ruler,
    description: "Modify existing jewelry",
    color: "bg-blue-100 text-blue-700",
  },
  {
    value: "POLISH_RHODIUM",
    label: "Polish/Rhodium",
    icon: Sparkles,
    description: "Cleaning and polishing work",
    color: "bg-cyan-100 text-cyan-700",
  },
  {
    value: "STONE_SETTING",
    label: "Stone Setting",
    icon: Gem,
    description: "Setting or replacing stones",
    color: "bg-pink-100 text-pink-700",
  },
  {
    value: "STOCK_ITEM",
    label: "Stock Item",
    icon: Package,
    description: "Making items for stock",
    color: "bg-green-100 text-green-700",
  },
  {
    value: "INTERNAL",
    label: "Internal Work",
    icon: Briefcase,
    description: "Internal/misc work",
    color: "bg-gray-100 text-gray-700",
  },
];

// ============================================
// WORK TYPES
// ============================================

export const WORK_TYPES = [
  { value: "NEW_MAKING", label: "New Making" },
  { value: "REPAIR", label: "Repair" },
  { value: "ALTERATION", label: "Alteration" },
  { value: "RESIZING", label: "Resizing" },
  { value: "POLISHING", label: "Polishing" },
  { value: "RHODIUM_PLATING", label: "Rhodium Plating" },
  { value: "STONE_REPLACEMENT", label: "Stone Replacement" },
  { value: "ENGRAVING", label: "Engraving" },
  { value: "HALLMARKING", label: "Hallmarking" },
  { value: "FINISHING", label: "Finishing" },
  { value: "ASSEMBLY", label: "Assembly" },
];

// ============================================
// PRIORITIES
// ============================================

export const PRIORITIES = [
  { value: "LOW", label: "Low", color: "bg-gray-100 text-gray-700" },
  { value: "NORMAL", label: "Normal", color: "bg-blue-100 text-blue-700" },
  { value: "HIGH", label: "High", color: "bg-orange-100 text-orange-700" },
  { value: "URGENT", label: "Urgent", color: "bg-red-100 text-red-700" },
  { value: "CRITICAL", label: "Critical", color: "bg-red-200 text-red-800" },
];

// ============================================
// METAL TYPES
// ============================================

export const METAL_TYPES = [
  { value: "GOLD", label: "Gold", icon: "🥇" },
  { value: "SILVER", label: "Silver", icon: "🥈" },
  { value: "PLATINUM", label: "Platinum", icon: "💎" },
];

// ============================================
// PRODUCT TYPES
// ============================================

export const PRODUCT_TYPES = [
  { value: "NECKLACE", label: "Necklace" },
  { value: "RING", label: "Ring" },
  { value: "EARRING", label: "Earring" },
  { value: "BANGLE", label: "Bangle" },
  { value: "BRACELET", label: "Bracelet" },
  { value: "PENDANT", label: "Pendant" },
  { value: "CHAIN", label: "Chain" },
  { value: "MANGALSUTRA", label: "Mangalsutra" },
  { value: "ANKLET", label: "Anklet" },
  { value: "NOSE_PIN", label: "Nose Pin" },
  { value: "MAANG_TIKKA", label: "Maang Tikka" },
  { value: "WAIST_CHAIN", label: "Waist Chain" },
  { value: "OTHER", label: "Other" },
];

// ============================================
// PURITIES
// ============================================

export const GOLD_PURITIES = [
  { value: "24K", label: "24K (999)", purity: 99.9 },
  { value: "22K", label: "22K (916)", purity: 91.6 },
  { value: "18K", label: "18K (750)", purity: 75.0 },
  { value: "14K", label: "14K (585)", purity: 58.5 },
];

export const SILVER_PURITIES = [
  { value: "999", label: "999 (Fine Silver)", purity: 99.9 },
  { value: "925", label: "925 (Sterling)", purity: 92.5 },
  { value: "900", label: "900", purity: 90.0 },
];

// ============================================
// LABOR RATE TYPES
// ============================================

export const LABOR_RATE_TYPES = [
  { value: "PER_GRAM", label: "Per Gram (₹/g)" },
  { value: "PER_PIECE", label: "Per Piece" },
  { value: "FIXED", label: "Fixed Amount" },
  { value: "PERCENTAGE", label: "Percentage (%)" },
];

// ============================================
// ITEM CONDITIONS
// ============================================

export const ITEM_CONDITIONS = [
  { value: "GOOD", label: "Good", color: "bg-green-100 text-green-700" },
  { value: "FAIR", label: "Fair", color: "bg-yellow-100 text-yellow-700" },
  { value: "DAMAGED", label: "Damaged", color: "bg-orange-100 text-orange-700" },
  { value: "NEEDS_CLEANING", label: "Needs Cleaning", color: "bg-blue-100 text-blue-700" },
  { value: "BROKEN", label: "Broken", color: "bg-red-100 text-red-700" },
];

// ============================================
// STONE TYPES
// ============================================

export const STONE_TYPES = [
  { value: "DIAMOND", label: "Diamond" },
  { value: "RUBY", label: "Ruby" },
  { value: "EMERALD", label: "Emerald" },
  { value: "SAPPHIRE", label: "Sapphire" },
  { value: "PEARL", label: "Pearl" },
  { value: "CUBIC_ZIRCONIA", label: "Cubic Zirconia" },
  { value: "POLKI", label: "Polki" },
  { value: "KUNDAN", label: "Kundan" },
  { value: "OTHER", label: "Other" },
];

// ============================================
// FORM STEPS
// ============================================

export const FORM_STEPS = [
  { id: 1, title: "Job Type", icon: Briefcase },
  { id: 2, title: "Details", icon: FileText },
  { id: 3, title: "Specifications", icon: Settings },
  { id: 4, title: "Images", icon: ImageIcon },
  { id: 5, title: "Timeline & Pricing", icon: Calendar },
  { id: 6, title: "Review", icon: CheckCircle },
];

// ============================================
// DEFAULT FORM VALUES
// ============================================

export const getDefaultFormValues = (initialData = {}) => ({
  title: initialData.title || "",
  description: initialData.description || "",
  jobType: initialData.jobType || "CUSTOM_ORDER",
  workType: initialData.workType || "NEW_MAKING",
  priority: initialData.priority || "NORMAL",
  customerName: initialData.customerName || "",
  customerPhone: initialData.customerPhone || "",
  customerId: initialData.customerId || "",
  // Product Reference
  productId: initialData.productId || "",
  productName: initialData.productName || "",
  productSKU: initialData.productSKU || "",
  categoryId: initialData.categoryId || "",
  categoryName: initialData.categoryName || "",
  // Item Details
  itemDetails: {
    description: initialData.itemDetails?.description || "",
    itemType: initialData.itemDetails?.itemType || "",
    metalType: initialData.itemDetails?.metalType || "",
    estimatedWeight: initialData.itemDetails?.estimatedWeight || "",
    condition: initialData.itemDetails?.condition || "",
    customerValuation: initialData.itemDetails?.customerValuation || "",
  },
  // Specifications
  specifications: {
    metalType: initialData.specifications?.metalType || "",
    productType: initialData.specifications?.productType || "",
    purity: initialData.specifications?.purity || "",
    weight: initialData.specifications?.weight || "",
    size: initialData.specifications?.size || "",
    hallmarkRequired: initialData.specifications?.hallmarkRequired ?? true,
    designReference: initialData.specifications?.designReference || "",
  },
  // Timeline
  expectedCompletionDate: initialData.expectedCompletionDate || "",
  estimatedHours: initialData.estimatedHours || "",
  // Pricing
  pricing: {
    estimatedLabor: initialData.pricing?.estimatedLabor || "",
    laborRateType: initialData.pricing?.laborRateType || "PER_GRAM",
    laborRate: initialData.pricing?.laborRate || "",
  },
  // Assignment
  karigarId: initialData.karigarId || "",
  // Flags
  isUrgent: initialData.isUrgent || false,
  requiresPickup: initialData.requiresPickup || false,
  requiresDelivery: initialData.requiresDelivery || false,
  // Instructions
  specialInstructions: initialData.specialInstructions || "",
  // Order reference
  orderId: initialData.orderId || "",
  orderItemIndex: initialData.orderItemIndex,
});

// ============================================
// JOB TYPE HELPERS
// ============================================

export const STANDALONE_JOB_TYPES = ["REPAIR", "ALTERATION", "POLISH_RHODIUM", "STONE_SETTING"];
export const PRODUCT_SELECTABLE_JOB_TYPES = ["CUSTOM_ORDER", "STOCK_ITEM", "NEW_MAKING"];
export const ITEM_DETAILS_JOB_TYPES = ["REPAIR", "ALTERATION"];

export const isStandaloneJob = (jobType) => STANDALONE_JOB_TYPES.includes(jobType);
export const canSelectProduct = (jobType) => 
  PRODUCT_SELECTABLE_JOB_TYPES.includes(jobType) || !ITEM_DETAILS_JOB_TYPES.includes(jobType);
export const needsItemDetails = (jobType) => ITEM_DETAILS_JOB_TYPES.includes(jobType);
export const needsCustomer = (jobType) => isStandaloneJob(jobType) && jobType !== "INTERNAL";

// ============================================
// WORK TYPE MAPPING
// ============================================

export const JOB_TYPE_TO_WORK_TYPE = {
  REPAIR: "REPAIR",
  ALTERATION: "ALTERATION",
  POLISH_RHODIUM: "POLISHING",
  STONE_SETTING: "STONE_REPLACEMENT",
  CUSTOM_ORDER: "NEW_MAKING",
  STOCK_ITEM: "NEW_MAKING",
  INTERNAL: "NEW_MAKING",
};
